<template>
  <v-app id="app">
    <AppHeader />
    <FlashMessage />
    <Toast />
    <router-view name="sidebar"></router-view>
    <v-main id="main">
      <ErrorBoundary class="tw:h-full">
        <VersionNotifier />
        <router-view />
      </ErrorBoundary>
      <DisclaimerModal disclaimer-type="LEGAL" v-if="showLegalDisclaimer()" />
      <ClientViewsModal v-if="showClientViewsModal" :show="showClientViewsModal" @enable-client-view="$router.push('/home')" />
    </v-main>
    <ClientViewOverlay v-if="isClientViewEnabled" />
  </v-app>
</template>

<script lang="ts">
import { provide } from 'vue';
import AppHeader from '@/components/Header.vue';
import FlashMessage from '@/components/FlashMessage.vue';
import Toast from '@/components/v2/Toast.vue';
import VersionNotifier from '@/components/VersionNotifier.vue';
import ErrorBoundary from '@/components/ErrorBoundary.vue';
import AppStore, { AppState, ResizerRegistryItem } from '@/store';
import { bootstrap, config } from 'vue-gtag';
import { Store, useStore } from 'vuex';
import DisclaimerModal from './components/DisclaimerModal.vue';
import { ActivityMixin } from '@/utilities/Activity';
import ClientViewOverlay from './components/ClientViewOverlay.vue';
import { computed } from 'vue';
import ClientViewsModal from './components/ClientViewsModal.vue';
import { registerResizeItem } from './utilities/resize';
import { IsUserAClient } from './utilities/AuthUtils';
import { useHeaderOffset } from '@/composables/useHeaderOffset';

export default {
  components: {
    ErrorBoundary,
    VersionNotifier,
    AppHeader,
    FlashMessage,
    Toast,
    DisclaimerModal,
    ClientViewOverlay,
    ClientViewsModal,
  },
  mixins: [ActivityMixin],
  methods: {
    handleResize() {
      for (const item of Object.values(this.store.state.resizerRegistry) as ResizerRegistryItem[]) {
        item.resizeFunc?.call(self, item.name, item.selector);
      }
    },
    showLegalDisclaimer(): boolean {
      const user = AppStore.state.user;
      if (user === undefined) {
        console.error('user is undefined');
        return false;
      }

      return IsUserAClient(user) && !user.legalDisclaimerAccepted;
    },
  },
  async created() {
    provide<Store<AppState>>('store', AppStore);
    const user = AppStore.state.user;
    const enableGtag = (): void => {
      bootstrap().then(() => {
        config({ user_email: user?.email });
      });
    };
    if (user) {
      enableGtag();
    }

    addEventListener('resize', this.handleResize);
  },
  mounted() {
    registerResizeItem({ name: 'app', selector: 'app' });
    this.handleResize();
  },
  setup() {
    const store = useStore<AppState>();
    const isClientViewEnabled = computed<boolean>(() => AppStore.state.clientViewState.isClientViewEnabled);
    const showClientViewsModal = computed<boolean>(() => AppStore.state.clientViewState.showClientViewsModal);
    
    const { totalOffset } = useHeaderOffset();

    return { store, isClientViewEnabled, showClientViewsModal, totalOffset };
  },
};
</script>

<style lang="scss" scoped>
@import 'assets/styles/global.css';

#app {
  font-size: 14px;
  color: var(--v-text-base);
}
</style>
